import { AccountInfo, BrowserUtils, InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import {
    ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import { UsersClient, UserDto, UserProfileDto } from '../api/web-api-client';
import setTargetOrganisation, { getTargetOrganisation } from '../storage/targetOrganisation';
import AccountContext, { AccountContextState, AccountDetails } from './accountContext';
import { tokenRequest } from './authConfig';
import termsData from '../terms-config.json';
import AppLogger from '../instrumentation/AppLogger';
import { BranchSelectionModalMode } from '../components/modals/BranchSelectorModal/enums';

interface AccountProviderProps {
    children: ReactNode;
}

const toAccountDetails = (
    account: AccountInfo,
    user: UserDto,
) : AccountDetails => {
    const currentTermsVersion = termsData.TermsVersion;
    const acceptedTermsAndCondition = !!((user && user.acceptedTerms === true)
                                        && (user.termsVersion?.toString() === currentTermsVersion));
    const accountCreationCompleted = !!((user.organisation && user.organisation?.accountCompleted === true));
    const accountContactCompleted = !!((user.contact && user.contact?.isCompleted === true));
    const tokenClaims = account.idTokenClaims as any;
    const businessContext = getTargetOrganisation();

    return {
        homeAccountId: account.homeAccountId,
        organisation: user.organisation?.name || '',
        trading: user.organisation?.businessOrTradingName || '',
        branch: user.organisation?.branchOrLocationName || '',
        abn: user.organisation?.abn || user.employerAbn || '',
        email: tokenClaims?.email,
        familyName: tokenClaims?.family_name,
        givenName: tokenClaims?.given_name,
        userAcceptedTermsOfUse: acceptedTermsAndCondition,
        accountCreationCompleted,
        accountContactCompleted,
        targetOrganisation: {
            targetOrganisationAbn: businessContext?.targetOrganisationAbn ?? '',
            targetOrganisationName: businessContext?.targetOrganisationName ?? '',
        },
        currentTermsVersion: currentTermsVersion ?? '',
        defaultOrganisationId: user.defaultOrganisationId,
        organisationCRMGuid: user.organisation?.crmGuid,
        organisationIsCompleted: user.organisation?.isCompleted || false,
        isDefaultOrganisation: user.defaultOrganisationId !== null,
        showBranchSelector: false,
        contactId: user.contactId,
        userProfile: user.userProfile as UserProfileDto,
    };
};

const AccountProvider = ({ children } : AccountProviderProps) => {
    const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null);
    const [userProfileDetails, setUserProfileDetails] = useState<UserProfileDto>();
    const [errored, setErrored] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { inProgress, accounts, instance } = useMsal();

    const setAgree = useCallback(() => {
        if (accountDetails) {
            setAccountDetails({ ...accountDetails, userAcceptedTermsOfUse: true });
        }
    }, [accountDetails]);

    const setUserProfile = useCallback(
        async (profile: UserProfileDto) => {
            if (accountDetails && profile !== undefined) {
                setIsLoading(true);
                try {
                    // Update local state first (optional)
                    setUserProfileDetails(profile);
                    setAccountDetails({ ...accountDetails, userProfile: profile });

                    // Call the API directly and return the Promise
                    const client = new UsersClient();
                    const tokenResult = await instance.acquireTokenSilent({
                        ...tokenRequest,
                        account: accounts[0],
                    });
                    client.setAuthToken(tokenResult.accessToken);

                    // Group dashboard properties for neater code
                    const tc = profile.testingCalibrationDashboard || {};
                    const pa = profile.patternApprovalDashboard || {};
                    await client.setUserProfile(
                        profile.firstName,
                        profile.lastName,
                        profile.email,
                        profile.services,
                        tc.filterStatusType,
                        tc.filterYearType,
                        tc.filterSortOrder,
                        tc.filtersChanged,
                        tc.filterCurrentPage,
                        tc.filterActiveTab,
                        tc.filterSearchText,
                        pa.filterStatusType,
                        pa.filterYearType,
                        pa.filterSortOrder,
                        pa.filtersChanged,
                        pa.filterCurrentPage,
                        pa.filterActiveTab,
                        pa.filterSearchText,
                    );
                    setIsLoading(false);
                    return true; // Success
                } catch (error) {
                    setIsLoading(false);
                    AppLogger.error('Failed to save userprofile', error as Error);
                    return false;
                }
            }
            return Promise.resolve(false);
        },
        [accountDetails, accounts, instance],
    );

    const setOrganisation = useCallback(async (abn: string, name: string) => {
        if (accountDetails?.targetOrganisation) {
            accountDetails.targetOrganisation.targetOrganisationAbn = abn;
            accountDetails.targetOrganisation.targetOrganisationName = name;
            setTargetOrganisation({
                targetOrganisationAbn: abn,
                targetOrganisationName: name,
            });
            setAccountDetails({
                ...accountDetails,
            });
        }
    }, [accountDetails]);

    const setCompleted = useCallback(() => {
        if (accountDetails) {
            accountDetails.accountCreationCompleted = true;
            setAccountDetails({
                ...accountDetails,
            });
        }
    }, [accountDetails]);

    const setContactCompleted = useCallback(() => {
        if (accountDetails) {
            accountDetails.accountContactCompleted = true;
            setAccountDetails({
                ...accountDetails,
            });
        }
    }, [accountDetails]);

    const setDefaultOrganisationId = useCallback((defaultOrganisationId: number | undefined, defaultOrganisationCRMId: string | undefined) => {
        if (accountDetails) {
            accountDetails.defaultOrganisationId = defaultOrganisationId;
            accountDetails.organisationCRMGuid = defaultOrganisationCRMId;
            setAccountDetails({
                ...accountDetails,
            });
        }
    }, [accountDetails]);

    const setOrganisationAndBranch = useCallback(async (organisationName: string, tradingName: string, branchName: string) => {
        if (accountDetails) {
            accountDetails.organisation = organisationName;
            accountDetails.trading = tradingName !== null ? tradingName : '';
            accountDetails.branch = branchName !== null ? branchName : '';
            setAccountDetails({ ...accountDetails });
        }
    }, [accountDetails]);

    const setShowBranchSelector = useCallback((show: boolean) => {
        if (accountDetails) {
            accountDetails.showBranchSelector = show;
            accountDetails.branchSelectionModalMode = BranchSelectionModalMode.SelectAndEditOrg;
            setAccountDetails({ ...accountDetails });
        }
    }, [accountDetails]);

    const setShowRFQSelectModal = useCallback((show: boolean, rfqId: string, callingPath: string) => {
        if (accountDetails) {
            accountDetails.showBranchSelector = show;
            accountDetails.branchSelectionModalMode = BranchSelectionModalMode.RFQSelectOrg;
            accountDetails.rfqId = rfqId;
            accountDetails.callingPath = callingPath;
            setAccountDetails({ ...accountDetails });
        }
    }, [accountDetails]);

    useEffect(() => {
        const loadAccountDetails = async () => {
            if (accounts.length > 0) {
                const account = accounts[0];
                const client = new UsersClient();
                const tokenResult = await instance.acquireTokenSilent({
                    ...tokenRequest,
                    account: accounts[0],
                });
                client.setAuthToken(tokenResult.accessToken);
                AppLogger.verbose('AccountProvider.loadAccountDetails', account);
                try {
                    const user = await client.signIn({});
                    const businessContext = getTargetOrganisation();
                    AppLogger.verbose('AccountProvider.BusinessContext', { account, businessContext });
                    if (user && !businessContext) {
                        const targetOrg = {
                            targetOrganisationAbn: user?.organisation?.abn ?? '',
                            targetOrganisationName: user?.organisation?.name ?? '',
                        };
                        setTargetOrganisation(targetOrg);
                        AppLogger.verbose('AccountProvider.TargetOrg', { targetOrg, account });
                    }
                    setAccountDetails(toAccountDetails(account, user));
                } catch (error) {
                    setErrored(true);
                    setAccountDetails(null);
                    await instance.handleRedirectPromise();
                    if (inProgress === InteractionStatus.None || inProgress === InteractionStatus.Startup) {
                        await instance.logoutRedirect({
                            account: instance.getActiveAccount(),
                            onRedirectNavigate: () => !BrowserUtils.isInIframe(),
                        });
                    }
                    AppLogger.error('Failed to load loadAccountDetails', error as Error);
                }
            }
        };
        if (inProgress === InteractionStatus.Logout) {
            setAccountDetails(null);
        } else if (
            inProgress === InteractionStatus.None
            && accounts.length > 0
            && accounts[0]?.homeAccountId
            && accountDetails?.homeAccountId !== accounts[0]?.homeAccountId) {
            loadAccountDetails();
        }
    }, [inProgress, accounts, accountDetails, accountDetails?.userProfile, instance]);

    const value: AccountContextState = useMemo(() => ({
        details: accountDetails,
        setAgree,
        isLoading,
        setCompleted,
        setContactCompleted,
        setDefaultOrganisationId,
        setTargetOrganisation: setOrganisation,
        setOrganisationAndBranch,
        setShowBranchSelector,
        setShowRFQSelectModal,
        setUserProfile,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [setAgree,
        setOrganisation,
        accountDetails,
        isLoading,
        setCompleted,
        setContactCompleted,
        setShowBranchSelector,
        setShowRFQSelectModal,
        setUserProfile]);

    return (
        <AccountContext.Provider value={value}>
            {/* {isLoading && (
                <BlockUISpinner>
                    <p>Loading...</p>
                </BlockUISpinner>
            )} */}
            {children}
        </AccountContext.Provider>
    );
};

export default AccountProvider;
